import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Box, CircularProgress, Paper, useTheme, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Map } from 'mapbox-gl';
import MapComponent from '../components/MapComponent';
import { RemoteNodeFormRef } from '../components/RemoteNodeForm';
import {
  BaseNodeFormRef,
  BaseNodeUpdateData,
} from '../components/BaseNodeForm';
import FormPanel from '../components/FormPanel';
import FilterControls from '../components/FilterControls';
import HeatmapLegend from '../components/HeatmapLegend';
import SiteInfoPanel from '../components/SiteInfoPanel';
import PathProfilePanel from '../components/PathProfilePanel';
import {
  useNearbyBaseNodes,
  BaseNode,
  SiteInfo,
} from '../hooks/useNearbyBaseNodes';
import { useRemoteNodeMarker } from '../hooks/useRemoteNodeMarker';
import { useHeatmapOverlay } from '../hooks/useHeatmapOverlay';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import type { RootState } from '../store/store';
import { selectSectorNearbyBNs } from '../store/selectors/servicePredictorSelectors';
import {
  clearRemoteNode,
  calculateLost,
  toggleShowInfoDiv,
  toggleShowBNRadiusDiv,
  setRemoveRemoteButton,
  updateBaseNodeFields,
  updateRemoteNodeCoordinates,
  setRemoteNodeFindByAddress,
  findBNs,
  setSelectedSite,
  clearSectorResults,
  clearBaseNode,
  setShowInfoDiv,
  setActiveHeight,
} from '../store/slices/servicePredictorSlice';
import {
  calculateRadiusFromBounds,
  isSignificantCenterChange,
  boundsToPlainObject,
  Bounds,
  getHeatmapDataForHeight,
} from '../utils/mapUtils';
import { debounce } from '../utils/debounce';
import { useCalculatePathlossSpeedVisualization } from '../hooks/useCalculatePathlossSpeedVisualization';
import { ARCS_MIN_ZOOM } from '../constants/mapConstants';

const ServicePredictor = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isValid, operator, operatorKey, email } = useAppSelector(
    (state: RootState) => state.auth
  );
  const remoteNode = useAppSelector(
    (state: RootState) => state.servicePredictor.remoteNode
  );
  const baseNode = useAppSelector(
    (state: RootState) => state.servicePredictor.baseNode
  );
  const baseNodeList = useAppSelector(
    (state: RootState) => state.servicePredictor.baseNodeList
  );
  const sectorResults = useAppSelector(
    (state: RootState) => state.servicePredictor.sectorResults
  );
  const sectorLoading = useAppSelector(
    (state: RootState) => state.servicePredictor.sectorLoading
  );
  const ui = useAppSelector((state: RootState) => state.servicePredictor.ui);
  const selectedSite = useAppSelector(
    (state: RootState) => state.servicePredictor.selectedSite
  );
  const { activeHeatmap, activeHeight, activeBands } = useAppSelector(
    (state: RootState) => state.servicePredictor.filters
  );

  const remoteNodeFormRef = useRef<RemoteNodeFormRef>(null);
  const baseNodeFormRef = useRef<BaseNodeFormRef>(null);
  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    -122.4194, 37.7749,
  ]); // Default to San Francisco
  const lastFindBNsCenterRef = useRef<[number, number] | null>(null);
  const lastFindBNsBoundsRef = useRef<Bounds | null>(null);

  const [isFormPanelMinimized, setIsFormPanelMinimized] = useState(false);

  useEffect(() => {
    if (!isValid || !operator) {
      navigate('/');
    }
  }, [isValid, operator, navigate]);

  const debouncedFindBNs = useRef(
    debounce((operator: string, operatorKey: string, map: Map) => {
      const bounds = map.getBounds();
      const boundsPlain = boundsToPlainObject(bounds);
      const radius = calculateRadiusFromBounds(boundsPlain);

      const center = map.getCenter();
      const lat = center.lat;
      const lng = center.lng;

      dispatch(
        findBNs({
          params: {
            operator,
            latitude: lat,
            longitude: lng,
            radius,
            operatorKey,
          },
        })
      );

      lastFindBNsCenterRef.current = [lng, lat];
      lastFindBNsBoundsRef.current = boundsPlain;
    }, 500) // 500ms debounce
  ).current;

  useEffect(() => {
    if (isValid && operator && operatorKey && !baseNodeList && mapInstance) {
      const bounds = mapInstance.getBounds();
      const boundsPlain = boundsToPlainObject(bounds);
      const radius = calculateRadiusFromBounds(boundsPlain);

      const center = mapInstance.getCenter();
      const lat = center.lat;
      const lng = center.lng;

      dispatch(
        findBNs({
          params: {
            operator,
            latitude: lat,
            longitude: lng,
            radius,
            operatorKey,
          },
        })
      );

      lastFindBNsCenterRef.current = [lng, lat];
      lastFindBNsBoundsRef.current = boundsPlain;
    }
  }, [isValid, operator, operatorKey, baseNodeList, mapInstance, dispatch]);

  useEffect(() => {
    if (isValid && operator && operatorKey && baseNodeList && mapInstance) {
      const boundsToUse = lastFindBNsBoundsRef.current;
      const isSignificant = isSignificantCenterChange(
        lastFindBNsCenterRef.current,
        mapCenter,
        boundsToUse
      );

      if (isSignificant) {
        debouncedFindBNs(operator, operatorKey, mapInstance);
      }
    }
  }, [
    mapCenter,
    isValid,
    operator,
    operatorKey,
    baseNodeList,
    mapInstance,
    debouncedFindBNs,
  ]);

  const handleClearRNMarker = useCallback(() => {
    dispatch(clearRemoteNode());
    dispatch(setRemoveRemoteButton(false));
  }, [dispatch]);

  const handleUpdateSector = useCallback((sector: string) => {
    console.log('Sector updated:', sector);
  }, []);

  const handleUpdateBaseNodeFields = useCallback((data: BaseNodeUpdateData) => {
    console.log('Base node fields updated:', data);
  }, []);

  const handleCalculatePathLoss = useCallback(async () => {
    if (!remoteNodeFormRef.current || !baseNodeFormRef.current) {
      return;
    }

    const isRemoteNodeValid = remoteNodeFormRef.current.isFormValid();
    const isBaseNodeValid = baseNodeFormRef.current.isFormValid();

    if (!isRemoteNodeValid || !isBaseNodeValid || !operatorKey || !operator) {
      return;
    }

    const bnObject = baseNodeFormRef.current.getJsonData();
    const rnObject = remoteNodeFormRef.current.getJsonData();
    const isMultiSector = baseNode.baseNodeOption === 'all';

    dispatch(
      calculateLost({
        params: {
          baseNodeData: bnObject,
          remoteNodeData: rnObject,
          operator,
          email: email || '',
          operatorKey,
          isMultiSector,
        },
      })
    );
  }, [dispatch, operator, operatorKey, email, baseNode.baseNodeOption]);

  const handleToggleBNRadius = useCallback(() => {
    dispatch(toggleShowBNRadiusDiv());
  }, [dispatch]);

  const handleMapLoad = useCallback((map: Map) => {
    setMapInstance(map);
  }, []);

  const handleMapCenterChange = useCallback(
    (center: [number, number]) => {
      if (mapInstance) {
        const boundsToUse = lastFindBNsBoundsRef.current;
        const isSignificant = isSignificantCenterChange(
          mapCenter,
          center,
          boundsToUse
        );

        if (isSignificant) {
          setMapCenter(center);
        }
      } else {
        setMapCenter(center);
      }
    },
    [mapInstance, mapCenter]
  );

  const handleMapClick = useCallback(
    (coordinates: [number, number]) => {
      const [lng, lat] = coordinates;
      dispatch(setRemoteNodeFindByAddress(false));
      dispatch(updateRemoteNodeCoordinates({ lat, lng }));
    },
    [dispatch]
  );

  const onArcRightClick = useCallback(
    (node: BaseNode) => {
      dispatch(
        updateBaseNodeFields({
          serial: node.serial,
          cell: node.cell,
          sector: node.sector,
          hostname: node.hostname,
          latitude: node.latitude,
          longitude: node.longitude,
          azimuth: node.azimuth || undefined,
          downtilt: node.downtilt || undefined,
          height_agl: node.height_agl,
          height_gps: node.height_gps,
          market: node.market,
          site: node.site,
          region: node.region,
          carrier: node.carrier || [],
          subscribers: node.subscribers,
        })
      );
    },
    [dispatch]
  );

  const handleSiteClick = useCallback(
    (site: SiteInfo) => {
      dispatch(setSelectedSite(site));

      if (mapInstance && site.location) {
        const { latitude, longitude } = site.location;
        const currentZoom = mapInstance.getZoom();
        const targetZoom =
          currentZoom < ARCS_MIN_ZOOM ? ARCS_MIN_ZOOM : currentZoom;

        mapInstance.easeTo({
          center: [longitude, latitude],
          zoom: targetZoom,
          duration: 500,
        });
      }
    },
    [mapInstance, dispatch]
  );

  const handleResultSelect = useCallback(
    (baseNode: BaseNode, remoteNode: any, remoteNodeList: any[]) => {
      dispatch(
        updateBaseNodeFields({
          serial: baseNode.serial,
          cell: baseNode.cell,
          sector: baseNode.sector,
          hostname: baseNode.hostname,
          latitude: baseNode.latitude,
          longitude: baseNode.longitude,
          azimuth: baseNode.azimuth || undefined,
          downtilt: baseNode.downtilt || undefined,
          height_agl: baseNode.height_agl,
          height_gps: baseNode.height_gps,
          market: baseNode.market,
          site: baseNode.site,
          region: baseNode.region,
          carrier: baseNode.carrier || [],
          subscribers: baseNode.subscribers,
        })
      );
      dispatch(setShowInfoDiv(true));
    },
    [dispatch]
  );

  const isMultiSector = useMemo(() => {
    return !!sectorResults?.sector_list;
  }, [sectorResults]);

  const sectorNearbyBNs = useAppSelector(selectSectorNearbyBNs);

  const displayBaseNodeList = useMemo(() => {
    return sectorNearbyBNs || baseNodeList;
  }, [sectorNearbyBNs, baseNodeList]);

  useNearbyBaseNodes({
    map: mapInstance,
    baseNodeList: displayBaseNodeList,
    showBNRadius: ui.showBNRadiusDiv,
    onArcRightClick: onArcRightClick,
    onSiteClick: handleSiteClick,
  });

  useRemoteNodeMarker({
    map: mapInstance,
    location: remoteNode.location,
  });

  const selectedBaseNode = useMemo(() => {
    if (!displayBaseNodeList || displayBaseNodeList.length === 0) {
      return null;
    }

    if (baseNode.serial) {
      const found = displayBaseNodeList.find(
        (bn: BaseNode) => bn.serial === baseNode.serial
      );
      if (found) {
        return found;
      }
    }

    if (activeHeatmap && activeHeight) {
      for (const bn of displayBaseNodeList) {
        if (bn.heatmap_list && bn.heatmap_list.length > 0) {
          const heatmapItem = getHeatmapDataForHeight(
            bn.heatmap_list,
            activeHeight
          );
          if (heatmapItem) {
            return bn;
          }
        }
      }
    }

    return null;
  }, [displayBaseNodeList, baseNode.serial, activeHeatmap, activeHeight]);

  const {
    isLoading: heatmapLoading,
    error: heatmapError,
    legendUrl,
  } = useHeatmapOverlay({
    map: mapInstance,
    baseNode: selectedBaseNode,
  });

  useCalculatePathlossSpeedVisualization({
    map: mapInstance,
    activeBaseNode: selectedBaseNode,
    onResultSelect: handleResultSelect,
  });

  useEffect(() => {
    if (
      remoteNode.location &&
      remoteNode.findByAddress &&
      remoteNode.addressUpdated
    ) {
      setMapCenter([remoteNode.location.lng, remoteNode.location.lat]);
    }
  }, [
    remoteNode.location,
    remoteNode.findByAddress,
    remoteNode.addressUpdated,
  ]);

  useEffect(() => {
    if (heatmapError) {
      console.error('Heatmap error:', heatmapError);
    }
  }, [heatmapError]);

  const handleToggleMinimize = useCallback(() => {
    setIsFormPanelMinimized((prev) => !prev);
  }, []);

  const handleClearMultiSector = useCallback(() => {
    dispatch(clearSectorResults());
    dispatch(clearBaseNode({ heatmap_exist: false, results_exist: false }));
    if (baseNodeFormRef.current) {
      baseNodeFormRef.current.clearInputs({
        heatmap_exist: false,
        results_exist: false,
      });
    }
  }, [dispatch]);

  const handleClosePathProfile = useCallback(() => {
    dispatch(setShowInfoDiv(false));
    dispatch(clearBaseNode({ heatmap_exist: false, results_exist: false }));
    dispatch(setActiveHeight(null));
  }, [dispatch]);

  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
      }}
    >
      <MapComponent
        height="100%"
        width="100%"
        style="mapbox://styles/mapbox/satellite-streets-v12"
        center={mapCenter}
        onMapLoad={handleMapLoad}
        onMapClick={handleMapClick}
        onCenterChange={handleMapCenterChange}
      />

      <FilterControls heatmapLoading={heatmapLoading} />
      {heatmapLoading && (
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <CircularProgress size={24} />
        </Paper>
      )}
      <HeatmapLegend legendUrl={legendUrl} position="bottom-right" />
      <SiteInfoPanel />
      {ui.showInfoDiv && <PathProfilePanel onClose={handleClosePathProfile} />}
      <FormPanel
        isMinimized={isFormPanelMinimized}
        onToggleMinimize={handleToggleMinimize}
        operator={operator}
        operatorKey={operatorKey}
        remoteNodeFormRef={remoteNodeFormRef}
        baseNodeFormRef={baseNodeFormRef}
        onClearRNMarker={handleClearRNMarker}
        onUpdateSector={handleUpdateSector}
        onUpdateBaseNodeFields={handleUpdateBaseNodeFields}
        onCalculatePathLoss={handleCalculatePathLoss}
        onToggleBNRadius={handleToggleBNRadius}
        onClearSectorResults={handleClearMultiSector}
        sectorLoading={sectorLoading}
        showSectorsButton={
          !!(baseNodeList && baseNodeList.length > 0) && !sectorResults
        }
        showRemoveButton={!!(ui.removeRemoteButton && sectorResults)}
        showClearButton={!!sectorResults}
        sectorsButtonLabel={
          ui.showBNRadiusDiv ? 'Hide Sectors' : 'Show Sectors'
        }
      />
    </Box>
  );
};

export default ServicePredictor;
